import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronDown, X, CheckCircle2, Loader2, Mail, Lock, Eye, EyeOff, User, Phone, Calendar } from 'lucide-react';
import supabase from '../supabase';
import { S } from '../constants';
import { GoogleSignInButton } from './GoogleSignInButton';

const DIAL = [{c:'+852',f:'🇭🇰',n:'HK'},{c:'+86',f:'🇨🇳',n:'CN'},{c:'+886',f:'🇹🇼',n:'TW'},{c:'+1',f:'🇺🇸',n:'US'},{c:'+44',f:'🇬🇧',n:'GB'},{c:'+81',f:'🇯🇵',n:'JP'},{c:'+82',f:'🇰🇷',n:'KR'},{c:'+65',f:'🇸🇬',n:'SG'},{c:'+66',f:'🇹🇭',n:'TH'},{c:'+61',f:'🇦🇺',n:'AU'},{c:'+49',f:'🇩🇪',n:'DE'},{c:'+33',f:'🇫🇷',n:'FR'}];
const validateEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const validatePw = p => p.length>=8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p);

// Multi-language field labels
const FIELD_LABELS = {
  username:    { 'zh-HK':'用戶名稱', 'zh-CN':'用户名称', 'en':'Username', 'ja':'ユーザー名', 'ko':'사용자 이름' },
  email:       { 'zh-HK':'電郵地址', 'zh-CN':'电子邮件', 'en':'Email Address', 'ja':'メールアドレス', 'ko':'이메일' },
  password:    { 'zh-HK':'密碼', 'zh-CN':'密码', 'en':'Password', 'ja':'パスワード', 'ko':'비밀번호' },
  confirmPw:   { 'zh-HK':'確認密碼', 'zh-CN':'确认密码', 'en':'Confirm Password', 'ja':'パスワード確認', 'ko':'비밀번호 확인' },
  phone:       { 'zh-HK':'電話號碼', 'zh-CN':'手机号码', 'en':'Phone Number', 'ja':'電話番号', 'ko':'전화번호' },
  birthday:    { 'zh-HK':'出生日期', 'zh-CN':'出生日期', 'en':'Birthday', 'ja':'生年月日', 'ko':'생년월일' },
  pwHint:      { 'zh-HK':'最少8位，含大寫、小寫及數字', 'zh-CN':'最少8位，含大写、小写及数字', 'en':'Min 8 chars, include uppercase, lowercase & number', 'ja':'8文字以上、大文字・小文字・数字を含む', 'ko':'8자 이상, 대문자·소문자·숫자 포함' },
  optional:    { 'zh-HK':'（選填）', 'zh-CN':'（选填）', 'en':'(Optional)', 'ja':'（任意）', 'ko':'(선택)' },
  usernameErr: { 'zh-HK':'請填寫用戶名稱', 'zh-CN':'请填写用户名称', 'en':'Username required', 'ja':'ユーザー名を入力してください', 'ko':'사용자 이름을 입력해 주세요' },
  // Placeholder text ("請輸入..." format)
  phUsername:  { 'zh-HK':'請輸入用戶名稱', 'zh-CN':'请输入用户名称', 'en':'Enter your username', 'ja':'ユーザー名を入力', 'ko':'사용자 이름 입력' },
  phEmail:     { 'zh-HK':'請輸入電郵地址', 'zh-CN':'请输入电子邮件', 'en':'Enter your email', 'ja':'メールアドレスを入力', 'ko':'이메일 입력' },
  phPassword:  { 'zh-HK':'請輸入密碼', 'zh-CN':'请输入密码', 'en':'Enter your password', 'ja':'パスワードを入力', 'ko':'비밀번호 입력' },
  phConfirmPw: { 'zh-HK':'請再次輸入密碼', 'zh-CN':'请再次输入密码', 'en':'Re-enter your password', 'ja':'パスワードを再入力', 'ko':'비밀번호 재입력' },
  phPhone:     { 'zh-HK':'請輸入電話號碼', 'zh-CN':'请输入手机号码', 'en':'Enter phone number', 'ja':'電話番号を入力', 'ko':'전화번호 입력' },
};
const lbl = (key, lang) => FIELD_LABELS[key]?.[lang] || FIELD_LABELS[key]?.['zh-HK'] || key;

export const AuthModal = ({ show, onClose, t, onLogin, inline=false, lang='zh-HK' }) => {
  const [mode, setMode] = useState('login');
  const [showPw, setShowPw] = useState(false);
  const [showCPw, setShowCPw] = useState(false);
  const [dial, setDial] = useState('+852');
  const [dialOpen, setDialOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const dialRef = useRef(null);
  const [f, setF] = useState({ name:'', email:'', pw:'', cpw:'', phone:'', bday:'', subscribeNews:true });
  const [errs, setErrs] = useState({});
  useEffect(() => {
    const h = e => { if(dialRef.current&&!dialRef.current.contains(e.target)) setDialOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  if (!show&&!inline) return null;

  const validate = (mode) => {
    const e={};
    if (!f.email) e.email=t.emailRequired;
    else if (!validateEmail(f.email)) e.email=t.emailInvalid;
    if (!f.pw) e.pw=t.pwRequired;
    else if (mode==='register'&&!validatePw(f.pw)) e.pw=t.pwPolicy;
    if (mode==='register'&&f.pw!==f.cpw) e.cpw=t.pwNoMatch;
    if (mode==='register'&&!f.name.trim()) e.name=lbl('usernameErr', lang);
    if (mode==='register'&&!f.phone) e.phone=t.phoneRequired;
    return e;
  };

  const inp = { width:'100%', background:'#ffffff', border:'1px solid #eaecf0', borderRadius:10, padding:'11px 14px 11px 40px', fontSize:13, fontWeight:500, outline:'none', color:'#1e293b', boxSizing:'border-box', transition:'border-color 0.15s' };
  const ic = { position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' };
  const errStyle = { fontSize:11, color:'#dc2626', marginTop:3 };
  const fieldLabel = { fontSize:11, fontWeight:700, color:'#64748b', marginBottom:4, display:'block', letterSpacing:0.3 };

  const body = (
    <div style={{ background:'white', borderRadius:inline?22:0, overflow:'hidden' }}>
      {/* Suppress browser autofill credential icon via global style */}
      <style>{`
        input::-webkit-credentials-auto-fill-button { visibility: hidden !important; display: none !important; }
        input::-webkit-contacts-auto-fill-button { visibility: hidden !important; display: none !important; }
      `}</style>
      <div style={{ padding:'20px 22px 15px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'white', zIndex:1 }}>
        <div>
          {mode==='forgot'&&<button onClick={()=>{setMode('login');setSent(false);}} style={{ display:'flex',alignItems:'center',gap:5,background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:12,fontWeight:700,marginBottom:4 }}><ChevronLeft size={13}/>{t.login}</button>}
          <h2 style={{ margin:0, fontSize:19, fontWeight:900, color:'#0f172a' }}>{mode==='login'?t.login:mode==='register'?t.register:t.forgotPw?.replace('？','').replace('?','')}</h2>
        </div>
        {!inline&&<button aria-label="關閉" onClick={onClose} style={{ width:30,height:30,borderRadius:'50%',background:'#f1f5f9',border:'none',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer' }}><X size={14}/></button>}
      </div>
      <div style={{ padding:'16px 22px 22px', display:'flex', flexDirection:'column', gap:11 }}>

        {/* ── Forgot password ── */}
        {mode==='forgot'&&(sent?(
          <div style={{ textAlign:'center', padding:'14px 0' }}>
            <div style={{ width:48,height:48,background:'#f0fdf4',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px' }}><CheckCircle2 size={26} color="#16a34a"/></div>
            <p style={{ fontWeight:900,fontSize:15,color:'#0f172a',margin:'0 0 6px' }}>{t.resetSent}</p>
            <p style={{ fontSize:12,color:'#64748b',margin:'0 0 16px' }}>{t.resetSentDesc}</p>
            <button onClick={()=>{setMode('login');setSent(false);}} style={S.btnDark}>{t.login}</button>
          </div>
        ):(<>
          <div>
            <label style={fieldLabel}>{lbl('email', lang)}</label>
            <div style={{ position:'relative' }}><Mail size={14} color="#94a3b8" style={ic}/><input type="email" autoComplete="email" placeholder={lbl('phEmail', lang)} value={f.email} onChange={e=>setF({...f,email:e.target.value})} style={inp}/></div>
          </div>
          <button onClick={async()=>{
            setAuthLoading(true); setAuthError('');
            const { error } = await supabase.auth.resetPasswordForEmail(f.email, { redirectTo: window.location.origin });
            setAuthLoading(false);
            if (error) setAuthError(error.message); else setSent(true);
          }} disabled={authLoading} style={S.btnDark}>{authLoading ? '...' : t.sendReset}</button>
        </>))}

        {/* ── Login ── */}
        {mode==='login'&&(<>
          <GoogleSignInButton lang={lang} onLogin={onLogin} onError={setAuthError}/>
          <div style={{ display:'flex',alignItems:'center',gap:8 }}><div style={{ flex:1,height:1,background:'#f1f5f9' }}/><span style={{ fontSize:11,color:'#94a3b8',fontWeight:700 }}>{t.orWith}</span><div style={{ flex:1,height:1,background:'#f1f5f9' }}/></div>
          <div>
            <label style={fieldLabel}>{lbl('email', lang)}</label>
            <div style={{ position:'relative' }}><Mail size={14} color="#94a3b8" style={ic}/><input type="email" autoComplete="email" placeholder={lbl('phEmail', lang)} value={f.email} onChange={e=>setF({...f,email:e.target.value})} style={{ ...inp, borderColor:errs.email?'#dc2626':'#eaecf0' }}/></div>
            {errs.email&&<p style={errStyle}>{errs.email}</p>}
          </div>
          <div>
            <label style={fieldLabel}>{lbl('password', lang)}</label>
            <div style={{ position:'relative' }}>
              <Lock size={14} color="#94a3b8" style={ic}/>
              <input type={showPw?'text':'password'} autoComplete="current-password" placeholder={lbl('phPassword', lang)} value={f.pw} onChange={e=>setF({...f,pw:e.target.value})} style={{ ...inp, paddingRight:40, borderColor:errs.pw?'#dc2626':'#eaecf0' }}/>
              <button onClick={()=>setShowPw(v=>!v)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8' }}>{showPw?<EyeOff size={14}/>:<Eye size={14}/>}</button>
            </div>
            {errs.pw&&<p style={errStyle}>{errs.pw}</p>}
          </div>
          <div style={{ textAlign:'right' }}><button onClick={()=>setMode('forgot')} style={{ background:'none',border:'none',color:'#f59e0b',fontSize:12,fontWeight:900,cursor:'pointer' }}>{t.forgotPw}</button></div>
          {authError&&<p style={{fontSize:11,color:'#dc2626',textAlign:'center',margin:0}}>{authError}</p>}
          <button onClick={async()=>{
            const e=validate('login'); setErrs(e);
            if(Object.keys(e).length) return;
            setAuthLoading(true); setAuthError('');
            const { error } = await supabase.auth.signInWithPassword({ email:f.email, password:f.pw });
            setAuthLoading(false);
            if (error) setAuthError(error.message); else onLogin?.();
          }} disabled={authLoading} style={S.btnDark}>{authLoading?<Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>:t.login}</button>
          <p style={{ textAlign:'center',fontSize:12,color:'#64748b',margin:0 }}>{t.noAcc} <button onClick={()=>{setMode('register');setErrs({});}} style={{ background:'none',border:'none',color:'#f59e0b',fontWeight:900,cursor:'pointer',fontSize:12 }}>{t.register}</button></p>
        </>)}

        {/* ── Register ── */}
        {mode==='register'&&(<>
          <GoogleSignInButton lang={lang} onLogin={onLogin} onError={setAuthError}/>
          <div style={{ display:'flex',alignItems:'center',gap:8 }}><div style={{ flex:1,height:1,background:'#f1f5f9' }}/><span style={{ fontSize:11,color:'#94a3b8',fontWeight:700 }}>{t.orWith}</span><div style={{ flex:1,height:1,background:'#f1f5f9' }}/></div>

          {/* Username */}
          <div>
            <label style={fieldLabel}>{lbl('username', lang)} <span style={{ color:'#ef4444' }}>*</span></label>
            <div style={{ position:'relative' }}>
              <User size={14} color="#94a3b8" style={ic}/>
              <input type="text" autoComplete="username" placeholder={lbl('phUsername', lang)} value={f.name} onChange={e=>setF({...f,name:e.target.value})} style={{ ...inp, borderColor:errs.name?'#dc2626':'#eaecf0' }}/>
            </div>
            {errs.name&&<p style={errStyle}>{errs.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label style={fieldLabel}>{lbl('email', lang)} <span style={{ color:'#ef4444' }}>*</span></label>
            <div style={{ position:'relative' }}><Mail size={14} color="#94a3b8" style={ic}/><input type="email" autoComplete="email" placeholder={lbl('phEmail', lang)} value={f.email} onChange={e=>setF({...f,email:e.target.value})} style={{ ...inp, borderColor:errs.email?'#dc2626':'#eaecf0' }}/></div>
            {errs.email&&<p style={errStyle}>{errs.email}</p>}
          </div>

          {/* Password */}
          <div>
            <label style={fieldLabel}>{lbl('password', lang)} <span style={{ color:'#ef4444' }}>*</span></label>
            <p style={{ fontSize:10, color:'#94a3b8', margin:'0 0 4px' }}>{lbl('pwHint', lang)}</p>
            <div style={{ position:'relative' }}>
              <Lock size={14} color="#94a3b8" style={ic}/>
              <input type={showPw?'text':'password'} autoComplete="new-password" placeholder={lbl('phPassword', lang)} value={f.pw} onChange={e=>setF({...f,pw:e.target.value})} style={{ ...inp, paddingRight:40, borderColor:errs.pw?'#dc2626':'#eaecf0' }}/>
              <button onClick={()=>setShowPw(v=>!v)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8' }}>{showPw?<EyeOff size={14}/>:<Eye size={14}/>}</button>
            </div>
            {errs.pw&&<p style={errStyle}>{errs.pw}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label style={fieldLabel}>{lbl('confirmPw', lang)} <span style={{ color:'#ef4444' }}>*</span></label>
            <div style={{ position:'relative' }}>
              <Lock size={14} color="#94a3b8" style={ic}/>
              <input type={showCPw?'text':'password'} autoComplete="new-password" placeholder={lbl('phConfirmPw', lang)} value={f.cpw} onChange={e=>setF({...f,cpw:e.target.value})} style={{ ...inp, paddingRight:40, borderColor:errs.cpw?'#dc2626':'#eaecf0' }}/>
              <button onClick={()=>setShowCPw(v=>!v)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8' }}>{showCPw?<EyeOff size={14}/>:<Eye size={14}/>}</button>
            </div>
            {errs.cpw&&<p style={errStyle}>{errs.cpw}</p>}
          </div>

          {/* Phone */}
          <div>
            <label style={fieldLabel}>{lbl('phone', lang)} <span style={{ color:'#ef4444' }}>*</span></label>
            <div style={{ display:'flex', gap:7 }} ref={dialRef}>
              <div style={{ position:'relative', flexShrink:0 }}>
                <button onClick={()=>setDialOpen(v=>!v)} style={{ display:'flex',alignItems:'center',gap:5,background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'11px 10px',cursor:'pointer',fontWeight:700,fontSize:12,whiteSpace:'nowrap' }}>
                  {DIAL.find(d=>d.c===dial)?.f} {dial} <ChevronDown size={10}/>
                </button>
                {dialOpen&&(
                  <div style={{ position:'absolute',top:'100%',left:0,zIndex:9999,background:'white',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'4px 0',boxShadow:'0 20px 40px rgba(0,0,0,0.1)',maxHeight:180,overflowY:'auto',minWidth:115 }}>
                    {DIAL.map(d=><button key={d.c} onClick={()=>{setDial(d.c);setDialOpen(false);}} style={{ display:'flex',width:'100%',alignItems:'center',gap:6,padding:'8px 12px',background:'none',border:'none',fontWeight:700,fontSize:11,cursor:'pointer',color:d.c===dial?'#f59e0b':'#475569' }}>{d.f} {d.n} <span style={{ marginLeft:'auto',color:'#94a3b8' }}>{d.c}</span></button>)}
                  </div>
                )}
              </div>
              <div style={{ position:'relative', flex:1 }}><Phone size={14} color="#94a3b8" style={ic}/><input type="tel" autoComplete="tel" placeholder={lbl('phPhone', lang)} value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} style={{ ...inp, borderColor:errs.phone?'#dc2626':'#eaecf0' }}/></div>
            </div>
            {errs.phone&&<p style={errStyle}>{errs.phone}</p>}
          </div>

          {/* Birthday */}
          <div>
            <label style={fieldLabel}>{lbl('birthday', lang)} <span style={{ color:'#94a3b8', fontSize:10 }}>{lbl('optional', lang)}</span></label>
            <div style={{ position:'relative' }}><Calendar size={14} color="#94a3b8" style={ic}/><input type="date" autoComplete="bday" value={f.bday} onChange={e=>setF({...f,bday:e.target.value})} style={{ ...inp, color:f.bday?'#1e293b':'#94a3b8' }}/></div>
          </div>

          {/* Subscribe */}
          <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', padding:'10px 14px', background:'#fffbeb', borderRadius:12, border:'1.5px solid #fde68a' }}>
            <input type="checkbox" checked={f.subscribeNews} onChange={e=>setF({...f,subscribeNews:e.target.checked})}
              style={{ width:16, height:16, accentColor:'#f59e0b', flexShrink:0, marginTop:1, cursor:'pointer' }}/>
            <div>
              <p style={{ margin:'0 0 2px', fontSize:12, fontWeight:800, color:'#92400e' }}>
                {lang==='en'?'✈️ Subscribe to Travel Newsletter':lang==='ja'?'✈️ 旅行ニュースレターを購読':lang==='ko'?'✈️ 여행 뉴스레터 구독':'✈️ 訂閱旅遊情報電郵通知'}
              </p>
              <p style={{ margin:0, fontSize:11, color:'#b45309', lineHeight:1.5 }}>
                {lang==='en'?'Get notified when new travel articles are published. Unsubscribe anytime.':lang==='ja'?'新しい旅行記事が公開されたときにメール通知が届きます。いつでも退会できます。':lang==='ko'?'새 여행 기사가 게시되면 이메일 알림을 받습니다. 언제든지 구독 취소 가능.':'新旅遊文章發佈時自動收到電郵通知，可隨時取消訂閱'}
              </p>
            </div>
          </label>
          {authError&&<p style={{fontSize:11,color:'#dc2626',textAlign:'center',margin:0}}>{authError}</p>}
          <button onClick={async()=>{
            const e=validate('register'); setErrs(e);
            if(Object.keys(e).length) return;
            setAuthLoading(true); setAuthError('');
            try {
              const chkRes = await fetch('/api/delete-account', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ action:'check', email:f.email }) });
              const chk = await chkRes.json();
              if (chk.blocked) { setAuthLoading(false); setAuthError(t.deletedCooldown); return; }
            } catch { /* if the check itself fails, fall through and let signUp proceed */ }
            const { error, data } = await supabase.auth.signUp({ email:f.email, password:f.pw, options:{ data:{ full_name:f.name.trim(), phone:`${dial}${f.phone}`, birthday:f.bday } } });
            if (!error && f.subscribeNews) {
              fetch('/api/subscribe', { method:'POST', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({ email:f.email, user_id:data?.user?.id||null, action:'subscribe' }) }).catch(()=>{});
            }
            setAuthLoading(false);
            if (error) setAuthError(error.message); else onLogin?.();
          }} disabled={authLoading} style={S.btnDark}>{authLoading?<Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/>:t.register}</button>
          <p style={{ textAlign:'center',fontSize:12,color:'#64748b',margin:0 }}>{t.hasAcc} <button onClick={()=>{setMode('login');setErrs({});}} style={{ background:'none',border:'none',color:'#f59e0b',fontWeight:900,cursor:'pointer',fontSize:12 }}>{t.login}</button></p>
        </>)}
      </div>
    </div>
  );
  if (inline) return body;
  return (
    <div style={{ position:'fixed',inset:0,zIndex:300,display:'flex',alignItems:'flex-end',justifyContent:'center',background:'rgba(0,0,0,0.55)',backdropFilter:'blur(8px)' }}
         onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{ width:'100%',maxWidth:440,borderRadius:'22px 22px 0 0',overflow:'hidden',maxHeight:'93vh',overflowY:'auto' }}>{body}</div>
    </div>
  );
};
